<template>
  <Toolbar class="p-mb-4 p-mt-4">
    <template #start>
      <div style="font-size: 1.4em; color: inherit" class="p-mr-3">{{ $t('Accounts') }}</div>
      <AddNewButton v-if="!cannotManageUserAccount"
                    :disabled="cannotCreateUserAccount"
                    :loading="employeeIsLoading"
                    :label="$t('Add')"
                    :class="{ 'p-mr-2' : user?.id && !hasUserAssignments && !cannotManageUserAccount}"
                    @click="openCreateUserAccount"/>
      <small v-if="user?.id && !hasUserAssignments && !cannotManageUserAccount"
             class="p-d-flex p-ai-center p-p-1 p-text-secondary">
        <i class="ti-info-alt p-mr-1"/>
        {{ $t('userAccountRequiresRoleAssignmentMsg') }}
      </small>
    </template>
  </Toolbar>

  <Spinner v-if="user && isLoading"/>
  <DesktopDataTable v-else
                    :dataTableIsLoading="dataTableIsLoading"
                    :itemsArrayIsEmpty="itemsArrayIsEmpty"
                    :sort="sort"
                    :showSearchInput="false"
                    :actionButtonsCount="actionButtonsCount"
                    @sort-items="sortItems"
                    :headers="[
              {name: 'username', title: $t('Username'), sortable: false},
              {name: 'label', title: $t('userAccountLabel'), sortable: false},
              {name: 'default_location_id', title: $t('defaultLocationLabel'), sortable: false},
              {name: 'default_role', title: $t('defaultRoleLabel'), sortable: false},
              {name: 'can_switch_location', title: $t('canSwitchLocationLabel'), sortable: false},
              {name: 'can_switch_role', title: $t('canSwitchRoleLabel'), sortable: false},
              {name: 'created_at', title: $t('Created'), width: '17%', sortable: true},
          ]">
    <template v-slot:body>
      <tr v-for="item of items" :key="item.id"
          :class="{'updated-item': isItemUpdated(item.id)}">

        <td class="table-data">
          <span v-if="item.username">{{ item.username }}</span>
          <i v-else class="ti-minus"/>
        </td>

        <td class="table-data">
          <span v-if="item.label">{{ item.label }}</span>
          <i v-else class="ti-minus"/>
        </td>

        <td class="table-data">
          <span v-if="item.default_location_id && item.location?.public_name">{{ item.location.public_name }}</span>
          <i v-else class="ti-minus"/>
        </td>

        <td class="table-data">
<!--          <span v-if="item.default_role">{{ roleLabel(item.default_role) }}</span>-->
          <UserRoleBadge v-if="item.default_role" :role="item.default_role"/>
          <i v-else class="ti-minus"/>
        </td>

        <td class="table-data">
          <i class="ti-check" v-if="+item.can_switch_location === 1"/>
          <i class="ti-close" v-else/>
        </td>

        <td class="table-data">
          <i class="ti-check" v-if="+item.can_switch_role === 1"/>
          <i class="ti-close" v-else/>
        </td>


        <td class="table-date-line">
          <span v-if="item.created_at">
            {{ formatBy(item.created_at, item.creator) }}
          </span>
          <i v-else class="ti-minus"/>
        </td>

        <td v-show="actionButtonsCount">
          <div class="actions">
            <EditButton :disabled="isRowActionDisabled(item)"
                        @click="editItem(item)" />
            <DeleteButton :disabled="isRowActionDisabled(item)"
                          @click="confirmItemDelete(item)"/>
          </div>
        </td>
      </tr>
    </template>
  </DesktopDataTable>

  <OwnPaginator v-show="!itemsArrayIsEmpty"
                :rows="pagination.perPage"
                :currentPage="pagination.currentPage - 1"
                :totalPages="pagination.pageCount"
                :totalRecords="pagination.totalCount"
                :rowsPerPageOptions="[5,10,25,50,100,200]"
                :pageLinkSize="11"/>

  <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>


  <UserAccountModal :visible="itemModal"
                    :item="item"
                    :userId="user?.id"
                    :locations="locations"
                    :accessMap="effectiveAccessMap"
                    @close="closeItemModal"
                    @update-items="updateItems"/>

  <ConfirmDeleteModal :visible="confirmItemDeleteModal"
                      :modalDataIsLoading="modalDataIsLoading"
                      @close="closeConfirmItemDeleteModal"
                      @delete-item="deleteItem"
                      :data="computedItemToDeleteData"/>
</template>

<script>
import httpClient from '@/services/http.services'
import datatableMixins from "@/mixins/datatableMixins";
import formatMixins from "@/mixins/formatMixins";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import UserAccountModal from "@/pages/employees/components/UserAccountModal.vue";
import httpMixins from "@/mixins/httpMixins";
import constants from "@/constants";
import UserRoleBadge from "@/components/UserRoleBadge.vue";

export default {
  mixins: [ showErrorsMixins, datatableMixins, formatMixins, httpMixins ],
  components: { UserAccountModal, UserRoleBadge },
  emits: ['update-items', 'updateItems'],
  props: {
    user: {
      type: Object,
      default: null,
    },
    employeeIsLoading: {
      type: Boolean,
      default: false
    },
  },
	data() {
		return {
      fieldsExpand: 'creator,location',
      apiLink: 'user-access/get-user-accounts',
      deleteItemUrl: 'user-access/delete-user-account',
      sort: {
        column: 'id',
        direction: '-',
      },
      locations: [],
		}
	},
  watch: {
    '$route'() {
      const route = this.$route.path.split('/')
      if (this.$route.path === '/login' || route.indexOf('employees') === -1 || route.indexOf('accounts') === -1) {
        return false
      }
      this.checkRouteQuery()
      this.getItems()
    },
  },
  mounted() {
    if (!this.$route.params.uid) return
    this.checkRouteQuery()
    this.$store.commit('SET_FETCH_IN_FLIGHT', false)
    this.getPageData()
  },
  methods: {
    getPageData() {
      const userId = this.user?.id
      if (!userId) return

      this.getItems()
      this.getLocations()
    },
    async getItems(showSpinner = false) {
      const userId = this.user?.id
      if (!userId || !this.userCanGetData || this.fetchInFlight) return
      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true
      this.disableEditButton = true
      if (showSpinner) {
        this.isLoading = true
      }
      const ctrl = this.setController?.('employeeUserAccounts')
      try {
        // const { status, data, headers } = await httpClient.post(`${this.apiLink}?page=${this.pagination.currentPage}&per-page=${this.pagination.perPage}&sort=${this.sort.direction + this.sort.column}&search=${this.searchData}&expand=${this.fieldsExpand}`, { id: this.$route.params.id })
        const { status, data, headers } = await httpClient({
          url: this.apiLink,
          method: 'post',
          params: {
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.processedSearchValue ?? null,
            expand: this.fieldsExpand,
          },
          data: {
            userId: userId,
            dateFrom: this.fromTimestamp,
            dateTo: this.toTimestamp,
            includeArchived: this.employeeIsArchived ? 1 : 0,
          },
          signal: ctrl?.signal,
        })

        if (status === 200 && data && Array.isArray(data)) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']
          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount  = 1
          }

          this.items = data

          if (!data.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch(err) {
        this.showError(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.isLoading = false
        this.dataTableIsLoading = false
        this.disableEditButton = false
        if (ctrl && this._abortors?.employeeUserAccounts === ctrl) {
          this._abortors.employeeUserAccounts = null
        }
      }
    },
    openCreateUserAccount() {
      if (this.cannotCreateUserAccount) return

      this.addNewItem()
    },
    isRowActionDisabled(item) {
      return this.dataTableIsLoading ||
          this.employeeIsLoading ||
          this.cannotManageUserAccount ||
          !!item?._savingFlag
    },

	},
  computed: {
    // cannotManageUserAccount() {
    //   const targetRole = Number(this.user?.max_role_with_archived || 0)
    //   const currentRole = Number(this.$store.getters.effectiveRole || 0)
    //   const roles = constants.userRoles
    //
    //   if (!currentRole) return true
    //
    //   if (this.employeeIsArchived && !this.isOwnerOrSuperAdmin) return true
    //
    //   if (currentRole === roles.superAdmin) return false
    //
    //   if (currentRole === roles.owner) {
    //     return targetRole === roles.superAdmin
    //   }
    //
    //   if (!targetRole) {
    //     return ![roles.admin].includes(currentRole)
    //   }
    //
    //   if (currentRole === roles.admin) {
    //     return ![
    //       roles.moderator,
    //       roles.employeeWorkshopMaster,
    //       roles.employeeMaster,
    //     ].includes(targetRole)
    //   }
    //
    //   return true
    // },
    cannotManageUserAccount() {
      const targetRole = Number(this.user?.max_role_with_archived || 0)
      const currentRole = Number(this.$store.getters.effectiveRole || 0)
      const roles = constants.userRoles

      if (!currentRole) return true

      if (this.employeeIsArchived && !this.isOwnerOrSuperAdmin) return true

      if (currentRole === roles.superAdmin) return false

      if (currentRole === roles.owner) {
        return targetRole === roles.superAdmin
      }

      if (!targetRole) {
        return ![
          roles.admin,
          roles.accountant,
        ].includes(currentRole)
      }

      if (
          currentRole === roles.admin ||
          currentRole === roles.accountant
      ) {
        return ![
          roles.moderator,
          roles.employeeWorkshopMaster,
          roles.employeeMaster,
        ].includes(targetRole)
      }

      return true
    },

    isOwnerOrSuperAdmin() {
      const role = Number(this.$store.getters.effectiveRole || 0)
      const roles = constants.userRoles

      return [roles.superAdmin, roles.owner].includes(role)
    },

    employeeIsArchived() {
      return !!this.user?.employee && Number(this.user.employee.status) !== 1
    },
    actionButtonsCount() {
      return !this.cannotManageUserAccount ? 2 : 0
    },
    computedItemToDeleteData() {
      if (!this.itemToDelete) return null

      return this.itemToDelete.username ?? null
    },
    hasUserAssignments() {
      return this.effectiveAccessMap.length > 0
    },

    userAccountsLimitReached() {
      return this.pagination.totalCount >= 3 &&
          this.$store.getters.effectiveRole !== constants.userRoles.superAdmin
    },

    cannotCreateUserAccount() {
      return this.employeeIsLoading ||
          this.dataTableIsLoading ||
          !this.user?.id ||
          !this.hasUserAssignments ||
          this.userAccountsLimitReached
    },
    effectiveAccessMap() {
      return this.employeeIsArchived
          ? (this.user?.archived_access_map || [])
          : (this.user?.access_map || [])
    },

  }
}
</script>
<style scoped lang="scss">
td {
  height: 44px;
}
</style>